<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="780px">
    <v-card class="profile-dialog-card entity-form-dialog application-edit-dialog">
      <div class="profile-dialog-header entity-form-dialog__header">
        <div class="entity-form-dialog__title">
          <v-avatar size="44" color="green lighten-5">
            <v-icon color="primary">mdi-file-account-outline</v-icon>
          </v-avatar>
          <div>
            <span>Applications</span>
            <h2>Edit application</h2>
            <p>Update contact and household information.</p>
          </div>
        </div>
        <v-btn icon aria-label="Close edit application dialog" @click.stop="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-card-text class="profile-dialog-body entity-form-dialog__body">
        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-account-multiple-outline</v-icon></span>
            <div>
              <h3>{{ isChildApplication ? "Parent information" : "Applicant information" }}</h3>
              <p>{{ isChildApplication ? "Primary and secondary contacts for this household." : "Primary contact information for this applicant." }}</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12" md="6">
              <div class="entity-form-dialog__subheading">
                {{ isChildApplication ? "Father / primary contact" : "Applicant" }}
              </div>
              <v-text-field
                v-model="Lead.fatherName"
                outlined
                dense
                autofocus
                :error-messages="fatherNameErrors"
                :hide-details="fatherNameErrors.length === 0"
                label="Full name"
              />
              <v-text-field
                v-model="Lead.fatherPhoneNumber"
                v-mask="'(###) ###-####'"
                type="tel"
                outlined
                dense
                :error-messages="fatherPhoneNumberErrors"
                :hide-details="fatherPhoneNumberErrors.length === 0"
                label="Phone number"
              />
              <v-text-field
                v-model="Lead.fatherEmailAddress"
                outlined
                dense
                :error-messages="fatherEmailAddressErrors"
                :hide-details="fatherEmailAddressErrors.length === 0"
                label="Email address"
                @change="fixFatherEmail"
              />
            </v-col>

            <v-col v-if="isChildApplication" cols="12" md="6">
              <div class="entity-form-dialog__subheading">Mother / secondary contact</div>
              <v-text-field
                v-model="Lead.motherName"
                outlined
                dense
                hide-details
                label="Full name"
              />
              <v-text-field
                v-model="Lead.motherPhoneNumber"
                v-mask="'(###) ###-####'"
                type="tel"
                outlined
                dense
                hide-details
                label="Phone number"
              />
              <v-text-field
                v-model="Lead.motherEmailAddress"
                outlined
                dense
                :error-messages="motherEmailAddressErrors"
                :hide-details="motherEmailAddressErrors.length === 0"
                label="Email address"
                @change="fixMotherEmail"
              />
            </v-col>
          </v-row>
        </section>

        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-home-outline</v-icon></span>
            <div>
              <h3>Household address</h3>
              <p>Address associated with this application.</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="Lead.address"
                outlined
                dense
                :error-messages="addressErrors"
                :hide-details="addressErrors.length === 0"
                label="Street address"
              />
            </v-col>
            <v-col cols="12" sm="5">
              <v-text-field
                v-model="Lead.city"
                outlined
                dense
                :error-messages="cityErrors"
                :hide-details="cityErrors.length === 0"
                label="City"
                @change="Lead.city = properCase(Lead.city)"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model="Lead.state"
                v-mask="'AA'"
                outlined
                dense
                :error-messages="stateErrors"
                :hide-details="stateErrors.length === 0"
                label="State"
              />
            </v-col>
            <v-col cols="6" sm="4">
              <v-text-field
                v-model="Lead.zip"
                v-mask="'#####'"
                outlined
                dense
                :error-messages="zipErrors"
                :hide-details="zipErrors.length === 0"
                label="ZIP code"
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="Lead.note"
                outlined
                dense
                hide-details
                auto-grow
                rows="2"
                :label="$t('labels.note')"
              />
            </v-col>
          </v-row>
        </section>

        <section v-if="isChildApplication" class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-phone-alert-outline</v-icon></span>
            <div>
              <h3>Emergency contact</h3>
              <p>Contact to use when parents are unavailable.</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="Lead.emergencyName"
                outlined
                dense
                :error-messages="emergencyNameErrors"
                :hide-details="emergencyNameErrors.length === 0"
                label="Full name"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="Lead.emergencyPhoneNumber"
                v-mask="'(###) ###-####'"
                type="tel"
                outlined
                dense
                :error-messages="emergencyPhoneNumberErrors"
                :hide-details="emergencyPhoneNumberErrors.length === 0"
                label="Phone number"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="Lead.emergencyRelation"
                outlined
                dense
                :error-messages="emergencyRelationErrors"
                :hide-details="emergencyRelationErrors.length === 0"
                label="Relationship"
              />
            </v-col>
          </v-row>
        </section>
      </v-card-text>

      <v-divider />

      <v-card-actions class="profile-dialog-actions entity-form-dialog__actions">
        <v-btn text @click="close()">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="loading" @click="submit()">
          <v-icon left small>mdi-content-save-outline</v-icon>
          Save changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import LeadService from "../service.js";
import GlobalFunction from "@/mixins/GlobalFunction";
import _ from "lodash";

const { validationMixin } = require("vuelidate");
const { required, email } = require("vuelidate/lib/validators");
const requiredForChild = (value, lead) =>
  Boolean(lead && lead.isChild === false) || required(value);

export default {
  props: {
    editDialog: {
      default: false,
    },
    editLead: {
      type: Object,
    },
  },
  name: "update-Lead",
  data() {
    return {
      leadStatus: ["Open", "Contacted", "Waiting", "Closed", "Convert"],
      loading: false,
      dialog: false,
      Student: {
        firstName: null,
        lastName: null,
        dob: null,
        selectedClass: null,
        ClassId: null,
      },
      Lead: {
        fatherName: null,
        fatherPhoneNumber: null,
        fatherEmailAddress: null,
        motherName: null,
        motherPhoneNumber: null,
        motherEmailAddress: null,
        emergencyName: null,
        emergencyPhoneNumber: null,
        emergencyRelation: null,
        address: null,
        city: null,
        state: "NY",
        zip: null,
      },
    };
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
    isChildApplication() {
      return this.Lead.isChild !== false;
    },
    studentFirstNameErrors() {
      const errors = [];
      if (!this.$v.Student.firstName.$dirty) return errors;
      if (!this.$v.Student.firstName.required)
        errors.push("First Name is required");
      return errors;
    },
    studentLastNameErrors() {
      const errors = [];
      if (!this.$v.Student.lastName.$dirty) return errors;
      if (!this.$v.Student.lastName.required)
        errors.push("Last Name is required");
      return errors;
    },
    dobError() {
      const errors = [];
      if (!this.$v.Student.dob.$dirty) return errors;
      if (!this.$v.Student.dob.required)
        errors.push("Date of birth is required");
      return errors;
    },
    classIdErrors() {
      const errors = [];
      if (!this.$v.Student.selectedClass.$dirty) return errors;
      if (!this.$v.Student.selectedClass.required)
        errors.push("Class is required");
      // if (!this.$v.Product.selectedClass.minValue) errors.push("Please Select Department");

      return errors;
    },
    genderErrors() {
      const errors = [];
      if (!this.$v.Student.gender.$dirty) return errors;
      if (!this.$v.Student.gender.required) errors.push("Gender is required");

      return errors;
    },
    // emergencyNameErrors() {
    //   const errors = [];
    //   if (!this.$v.Lead.emergencyName.$dirty) return errors;
    //   if (!this.$v.Lead.emergencyName.required)
    //     errors.push("Name is required");
    //   return errors;
    // },
    // emergencyPhoneNumberErrors() {
    //   const errors = [];
    //   if (!this.$v.Lead.emergencyPhoneNumber.$dirty) return errors;
    //   if (!this.$v.Lead.emergencyPhoneNumber.required)
    //     errors.push("Phone Number is required");
    //   return errors;
    // },
    // emergencyRelationErrors() {
    //   const errors = [];
    //   if (!this.$v.Lead.emergencyRelation.$dirty) return errors;
    //   if (!this.$v.Lead.emergencyRelation.required)
    //     errors.push("Relation is required");
    //   return errors;
    // },
    fatherNameErrors() {
      const errors = [];
      if (!this.$v.Lead.fatherName.$dirty) return errors;
      if (!this.$v.Lead.fatherName.required) errors.push("Name is required");
      return errors;
    },
    fatherPhoneNumberErrors() {
      const errors = [];
      if (!this.$v.Lead.fatherPhoneNumber.$dirty) return errors;
      if (!this.$v.Lead.fatherPhoneNumber.required)
        errors.push("Phone Number is required");
      return errors;
    },
    fatherEmailAddressErrors() {
      const errors = [];
      if (!this.$v.Lead.fatherEmailAddress.$dirty) return errors;
      if (!this.$v.Lead.fatherEmailAddress.required)
        errors.push("Email Address is required");
      if (!this.$v.Lead.fatherEmailAddress.email)
        errors.push("Email is not valid");
      return errors;
    },
    motherEmailAddressErrors() {
      const errors = [];
      if (!this.$v.Lead.motherEmailAddress.$dirty) return errors;
      if (!this.$v.Lead.motherEmailAddress.email)
        errors.push("Email is not valid");
      return errors;
    },
    addressErrors() {
      const errors = [];
      if (!this.$v.Lead.address.$dirty) return errors;
      if (!this.$v.Lead.address.required) errors.push("Address is required");
      return errors;
    },
    cityErrors() {
      const errors = [];
      if (!this.$v.Lead.city.$dirty) return errors;
      if (!this.$v.Lead.city.required) errors.push("City is required");
      return errors;
    },
    stateErrors() {
      const errors = [];
      if (!this.$v.Lead.state.$dirty) return errors;
      if (!this.$v.Lead.state.required) errors.push("State is required");
      return errors;
    },
    zipErrors() {
      const errors = [];
      if (!this.$v.Lead.zip.$dirty) return errors;
      if (!this.$v.Lead.zip.required) errors.push("Zip Code is required");
      return errors;
    },
    emergencyNameErrors() {
      const errors = [];
      if (!this.$v.Lead.emergencyName.$dirty) return errors;
      if (!this.$v.Lead.emergencyName.required)
        errors.push("Name is required");
      return errors;
    },
    emergencyPhoneNumberErrors() {
      const errors = [];
      if (!this.$v.Lead.emergencyPhoneNumber.$dirty) return errors;
      if (!this.$v.Lead.emergencyPhoneNumber.required)
        errors.push("Phone Number is required");
      return errors;
    },
    emergencyRelationErrors() {
      const errors = [];
      if (!this.$v.Lead.emergencyRelation.$dirty) return errors;
      if (!this.$v.Lead.emergencyRelation.required)
        errors.push("Relation is required");
      return errors;
    },
  },
  watch: {
    editDialog(val) {
      if (val && this.editLead) {
        this.Lead = _.cloneDeep(this.editLead);
      }
      this.dialog = val;
    },
  },
  mixins: [validationMixin, GlobalFunction],
  validations: {
    Lead: {
      fatherName: {
        required,
      },
      fatherPhoneNumber: {
        required,
      },
      fatherEmailAddress: {
        required,
        email,
      },
      motherEmailAddress: {
        email,
      },
      emergencyName: {
        required: requiredForChild,
      },
      emergencyPhoneNumber: {
        required: requiredForChild,
      },
      emergencyRelation: {
        required: requiredForChild,
      },
      address: {
        required,
      },
      city: {
        required,
      },
      state: {
        required,
      },
      zip: {
        required,
      },
    },
    // Student: {
    //   firstName: {
    //     required,
    //   },
    //   lastName: {
    //     required,
    //   },
    //   dob: {
    //     required,
    //   },
    //   gender: {
    //     required,
    //   },
    //   selectedClass: {
    //     required,
    //   },
    // },
  },
  methods: {
    fixFatherEmail() {
      if (this.Lead.fatherEmailAddress) {
        this.Lead.fatherEmailAddress = this.Lead.fatherEmailAddress
          .toLowerCase()
          .trim();
      }
    },
    fixMotherEmail() {
      if (this.Lead.motherEmailAddress) {
        this.Lead.motherEmailAddress = this.Lead.motherEmailAddress
          .toLowerCase()
          .trim();
      }
    },
    close() {
      this.empty();
      this.$emit("close");
    },
    empty() {
      this.Lead = {
        name: null,
        email: null,
        phoneNumber: null,
        contactName: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        note: null,
        status: "Hot Lead",
      };
      this.$v.$reset();
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        if (this.Lead.fatherPhoneNumber) {
          this.Lead.fatherPhoneNumber = this.Lead.fatherPhoneNumber.replace(
            /[^a-zA-Z0-9]/g,
            ""
          );
        }

        if (this.Lead.motherPhoneNumber) {
          this.Lead.motherPhoneNumber = this.Lead.motherPhoneNumber.replace(
            /[^a-zA-Z0-9]/g,
            ""
          );
        }

        if (this.Lead.emergencyPhoneNumber) {
          this.Lead.emergencyPhoneNumber =
            this.Lead.emergencyPhoneNumber.replace(/[^a-zA-Z0-9]/g, "");
        }
        this.loading = true;
        return LeadService.update(this.Lead.id, this.Lead)
          .then((result) => {
            if (result.status) {
              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "success",
                  title: "Application updated",
                });
              this.loading = false;
              this.$emit("submit");
              this.close();
            }
          })
          .catch((err) => {
            this.loading = false;
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: err && err.data ? err.data.message : "Application could not be updated",
              });
          });
      }
    },
  },
};
</script>

<style scoped></style>
